import { useEffect, useState } from "react";
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis, Legend } from "recharts";
import { Eye, UserRound, MessagesSquare, Radio } from "lucide-react";
import { useAuthedGet } from "@/pages/Admin";

const RANGES = [[7, "7 nap"], [30, "30 nap"], [90, "90 nap"], [180, "180 nap"]];
const PAGE_LABEL = { "/": "Főoldal", "/chat": "Chat", "/aszf": "ÁSZF", "/adatvedelem": "Adatvédelem" };
const DEVICE_LABEL = { mobile: "Mobil", desktop: "Számítógép", tablet: "Tablet" };

const Card = ({ icon: I, label, today, range, color, testId, suffix = " ma" }) => (
  <div className="rounded-3xl border border-white/10 bg-[#1A152E] p-5" data-testid={testId}>
    <I className="h-5 w-5" style={{ color }} />
    <p className="mt-4 font-display text-3xl font-black">{today ?? "…"}</p>
    <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground">{label}{suffix}</p>
    {range !== undefined && <p className="mt-2 text-sm text-muted-foreground">Időszakban: <b className="text-white">{range}</b></p>}
  </div>
);

const BarList = ({ title, rows, labels = {}, color, testId }) => {
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <div className="rounded-3xl border border-white/10 bg-[#1A152E] p-5" data-testid={testId}>
      <h3 className="mb-4 font-display text-lg font-bold">{title}</h3>
      {rows.length === 0 && <p className="text-sm text-muted-foreground">Még nincs adat.</p>}
      <div className="space-y-2">
        {rows.map((r) => (
          <div key={r.key} className="relative overflow-hidden rounded-xl bg-white/[0.03] px-3 py-2 text-sm">
            <div className="absolute inset-y-0 left-0 opacity-25" style={{ width: `${(r.count / max) * 100}%`, background: color }} />
            <div className="relative flex justify-between gap-2"><span className="truncate">{labels[r.key] || r.key}</span><b>{r.count}</b></div>
          </div>
        ))}
      </div>
    </div>
  );
};

export const AnalyticsPanel = () => {
  const req = useAuthedGet();
  const [days, setDays] = useState(30);
  const [d, setD] = useState(null);

  useEffect(() => {
    const load = () => req("/admin/analytics", { params: { days } }).then((r) => setD(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [days]); // eslint-disable-line

  const series = (d?.series || []).map((s) => ({ ...s, label: s.day.slice(5).replace("-", ".") }));

  return (
    <div className="space-y-6" data-testid="admin-analytics-panel">
      <div className="flex flex-wrap gap-2">
        {RANGES.map(([k, l]) => (
          <button key={k} onClick={() => setDays(k)} data-testid={`analytics-range-${k}`}
            className={`rounded-full px-4 py-1.5 text-xs font-bold transition-colors ${days === k ? "bg-[#00F0FF] text-[#0F0C1B]" : "bg-white/5 hover:bg-white/10"}`}>{l}</button>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Card testId="analytics-views" icon={Eye} label="Oldalmegtekintés" today={d?.today.views} range={d?.range.views} color="#FF3B6C" />
        <Card testId="analytics-visitors" icon={UserRound} label="Látogató" today={d?.today.visitors} range={d?.range.visitors} color="#00F0FF" />
        <Card testId="analytics-chats" icon={MessagesSquare} label="Beszélgetés" today={d?.today.chats} range={d?.range.chats} color="#FFD600" />
        <Card testId="analytics-online" icon={Radio} label="Online most" suffix="" today={d?.online} color="#00E676" />
      </div>
      <div className="overflow-hidden rounded-3xl border border-white/10 bg-[#1A152E] p-5" data-testid="analytics-chart">
        <h3 className="mb-4 font-display text-lg font-bold">Napi nézettség</h3>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={series} margin={{ left: -20, right: 8 }}>
              <defs>
                {[["v", "#FF3B6C"], ["u", "#00F0FF"], ["c", "#FFD600"]].map(([id, c]) => (
                  <linearGradient key={id} id={id} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={c} stopOpacity={0.45} /><stop offset="100%" stopColor={c} stopOpacity={0} />
                  </linearGradient>
                ))}
              </defs>
              <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
              <XAxis dataKey="label" stroke="#8b85a8" fontSize={11} tickLine={false} axisLine={false} minTickGap={16} />
              <YAxis stroke="#8b85a8" fontSize={11} tickLine={false} axisLine={false} allowDecimals={false} />
              <Tooltip contentStyle={{ background: "#241E3D", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 14 }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Area type="monotone" dataKey="views" name="Megtekintés" stroke="#FF3B6C" fill="url(#v)" strokeWidth={2} />
              <Area type="monotone" dataKey="visitors" name="Látogató" stroke="#00F0FF" fill="url(#u)" strokeWidth={2} />
              <Area type="monotone" dataKey="chats" name="Beszélgetés" stroke="#FFD600" fill="url(#c)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div className="grid gap-4 lg:grid-cols-3">
        <BarList testId="analytics-referrers" title="Honnan jöttek" rows={d?.referrers || []} labels={{ direct: "Közvetlen / ismeretlen" }} color="#FF3B6C" />
        <BarList testId="analytics-pages" title="Oldalak" rows={d?.pages || []} labels={PAGE_LABEL} color="#7C3AED" />
        <BarList testId="analytics-devices" title="Eszközök" rows={d?.devices || []} labels={DEVICE_LABEL} color="#00F0FF" />
      </div>
      <p className="text-xs text-muted-foreground">A számlálás sütik nélkül, anonim módon történik: a látogatót egy naponta változó, visszafejthetetlen kód azonosítja, IP-címet nem tárolunk.</p>
    </div>
  );
};
